/**
 * @file 文件相关的 utils
 */
import { last, round } from './lodash';
import { isDataURL } from './url';
const unitList = ['B', 'K', 'M', 'G', 'T'];
/**
 * 将文件大小从字节转化为用指定的或者最合适的单位来描述
 *
 * @category File
 * @param bytes - 字节数
 * @param unit - 可指定单位: B | KB | MB | GB | TB
 * @returns 转换单位后的文件大小描述 (数字部分最多保留两位小数)
 */
export function formatFileSize(bytes, unit) {
    let size = bytes;
    let unitIndex = 0;
    while (size >= 1024 && unitList[unitIndex] !== unit) {
        size /= 1024;
        unitIndex += 1;
    }
    return `${round(size, 2)}${unitList[unitIndex]}`;
}
/**
 * 将指定了单位的文件大小转化为比特值
 *
 * @category File
 * @param fileSize - 文件大小，即 1B/1KB/1MB/1GB/1TB
 * @returns bytes
 */
export function covertFileSizeToBytes(fileSize) {
    // 匹配正数及浮点数
    const [, matchSize, matchUnit] = fileSize.match(/([+]\d+[.]\d+|[-]\d+[.]\d+|\d+[.]\d+|[+]\d+|[-]\d+|\d+)(\D*)/i) || [];
    const size = Number(matchSize);
    const unit = matchUnit === null || matchUnit === void 0 ? void 0 : matchUnit.toLocaleLowerCase();
    if (!size)
        return 0;
    if (!unit)
        return size;
    const unitIndex = unitList.findIndex(_u => 
    // 适配 K or KB 的写法
    unit === _u.toLocaleLowerCase() || unit === `${_u}B`.toLocaleLowerCase());
    if (unitIndex < 0)
        return size;
    return Math.round(size * 1024 ** unitIndex);
}
/**
 * 下载 Blob 格式的文件
 *
 * @param name - 文件名
 * @param blob - Blob 文件
 */
function downloadBlobFile(name, blob) {
    const file = document.createElement('a');
    file.download = name;
    const blobURL = window.URL.createObjectURL(blob);
    file.href = blobURL;
    file.dispatchEvent(new MouseEvent('click'));
    window.URL.revokeObjectURL(blobURL);
}
/**
 * 从 Blob, web url 或 data url (比如由 canvas.toDataURL 得到) 下载文件并指定文件名
 *
 * @category File
 * @param name - 文件名
 * @param src - Blob, web url 或 data url
 */
export function downloadFile(name, src) {
    if (src instanceof Blob) {
        downloadBlobFile(name, src);
    }
    else if (isDataURL(src)) {
        const file = document.createElement('a');
        file.download = name;
        file.href = src;
        file.dispatchEvent(new MouseEvent('click'));
    }
    else {
        fetch(src).then(res => {
            res.blob().then(blob => downloadBlobFile(name, blob));
        });
    }
}
// 希悦网盘文件链接适配
const netdiskFileUrlRegExp = /netdisk\/files\/(.*)\/url/;
/**
 * 从文件名或文件 URL 中获取扩展名 (统一转为小写), 若没有则返回空字符串
 *
 * @category File
 * @param filenameOrURL - 文件名或文件 URL
 * @returns 扩展名
 */
export function getExtName(filenameOrURL) {
    var _a, _b;
    if (!filenameOrURL)
        return '';
    const matchNetdiskFile = filenameOrURL.match(netdiskFileUrlRegExp);
    let filename = '';
    if (matchNetdiskFile === null || matchNetdiskFile === void 0 ? void 0 : matchNetdiskFile.length) {
        ;
        [, filename] = matchNetdiskFile !== null && matchNetdiskFile !== void 0 ? matchNetdiskFile : '';
    }
    else {
        filename = (_a = last(filenameOrURL.split('/'))) !== null && _a !== void 0 ? _a : '';
    }
    const parts = filename.split('.');
    if (parts.length <= 1)
        return '';
    return ((_b = last(parts)) !== null && _b !== void 0 ? _b : '').toLowerCase();
}
//# sourceMappingURL=data:application/json;base64,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